import {
  pagination_queries_interface,
  pagination_response_interface,
} from "./Core/Pagination";
import { productsInterface } from "./products";
import { getTranslatedSaleStatus } from "../translates/sales/sales";
import { TokenRedemption } from "./tokens";

export interface salesQueries extends pagination_queries_interface {
  seller?: string;
  concession?: string;
  event?: string;
  session?: string;
  sale_point?: string;
  status?: string;
  search_by?: string;
  search_value?: string;
  venue?: string;
  tags?: string;
  payment_method?: string;
  is_system?: string;
}

export interface detailedSalesQueries {
  concession: string;
  event: string;
  session: string;
  venue: string;
  limit: number;
}

export interface salesListPagination extends pagination_response_interface {
  data: Sale[];
}

export interface Sale {
  products: productsInterface[];
  concession: Concession;
  created_at: string;
  event: Event;
  observations: string;
  seller: Seller;
  status: number;
  subtotal: number;
  tip: number;
  total: number;
  uuid: string;
  uuid_concession: string;
  uuid_event: string;
  uuid_seller: string;
  uuid_venue: string;
  total_products: number;
  fee: number;
  app_service_fee: number;
  app_order_type?: number | null;
  total_with_fee: number;
  venue: Venue;
  seller_name: string;
  event_name: string;
  payment_method?: string;
  order_type_tag?: string;
  type?: string;
  /**
   * YOS-10255: seller key holder who made the sale when a seller key was validated on the
   * terminal; `seller` keeps the primary (automatic) session.
   */
  secondary_seller?: SecondarySeller | null;
}

export interface SecondarySeller {
  uuid: string;
  name: string;
  code: string;
}

export interface TipAdjustmentSaleFields {
  type?: string;
}

interface Venue {
  uuid: string;
  short_name: string;
}

interface Seller {
  uuid: string;
  username: string;
  name: string;
}

interface Event {
  uuid: string;
  starts_at: string;
  ends_at: string;
  name: string;
}

interface Concession {
  uuid: string;
  image: string;
  name: string;
}

/////////

export interface Card {
  uuid: string;
  bin: string;
  last_four: string;
  brand: string;
  bank: string;
  type: string;
}

export interface NfcTransaction {
  transaction_id: string;
  uuid: string;
  uuid_sale: string;
  id_sale: number;
  previous_balance: number;
  post_balance: number;
  nfc_card: NfcCard;
}

interface NfcCard {
  uuid: string;
  serial_number: string;
  bin: string;
}

export interface Product {
  fee: number;
  product: string;
  price: number;
  base_price: number;
  quantity: number;
  subtotal: number;
  name_concession: string;
  uuid: string;
  uuid_concession: string;
  uuid_parent: string | null;
  uuid_product: string;
  uuid_sale: string;
  created_at: string;
  /**
   * True when the product has at least one active inventory supply (insumo) linked.
   * When `false`, the restock step shows an informative message instead of the Sí/No toggle.
   */
  has_supplies?: boolean;
  discount?: Discount;
  discountName: string;
  promotion?: Promotion;
  combo_name?: string;
  token_redemption?: TokenRedemption;
  /**
   * YOS-10255: status of the kitchen order this product belongs to, as the sale detail resolves it —
   * the highest active status, or null when the product never went to the kitchen. Drives the extra
   * confirmation before cancelling something already in preparation.
   * Compare against `ORDER_STATUSES_IN_PREPARATION`.
   */
  order_status?: number | null;
}

interface Discount {
  uuid: string;
  name: string;
  value: number;
  is_percent: boolean;
}

interface Promotion {
  uuid: string;
  name: string;
}

export enum SaleStatus {
  Cancelada = 0,
  Preautorizada = 1,
  Pagada = 2,
  "Reembolso pendiente" = 3,
  Reembolsada = 4,
  ParcialmentePagada = 5,
  CuentaAbierta = 6,
  Expirada = 7,
  PorConfirmar = 8,
}

export const saleStatus: { [key: string]: string } = {
  0: "Cancelada",
  1: "Preautorizada",
  2: "Pagada",
  3: "Reembolso pendiente",
  4: "Reembolsada",
  5: "Parcialmente pagada",
  6: "Cuenta Abierta",
  7: "Expirada",
};

// Helper function to get translated sale status
export const getSaleStatus = (status: number): string => {
  return getTranslatedSaleStatus(status);
};
///////////////////////something

export interface AppOrderSummary {
  customer_name: string;
  customer_phone_number: string;
  order_type: number;
  status: string;
  delivery_method: string;
  delivery_keyword: string;
  service_zone_name: string;
  section_name: string;
  pickup_point_name: string;
  seat_row: string | null;
  seat_number: string | null;
  scheduled_delivery_time: string | null;
  delivery_due_at: string | null;
}

export interface SalesDetail {
  subtotal: number;
  total: number;
  products: Product[];
  fee: number;
  grand_total: number;
  concession: Concession;
  created_at: string;
  updated_at: string;
  event: Event;
  code: string;
  sold_at: string;
  seller: Seller;
  /**
   * YOS-10255: seller key holder who made the sale when a seller key was validated on the
   * terminal; `seller` keeps the primary (automatic) session. Same shape the list already carries.
   */
  secondary_seller?: SecondarySeller | null;
  status: number;
  tip: number;
  app_service_fee: number;
  app_order_type?: number | null;
  app_order?: AppOrderSummary | null;
  uuid: string;
  uuid_concession: string;
  uuid_event: string;
  uuid_seller: string;
  uuid_venue: string;
  total_products: number;
  venue: Venue;
  payments: Payment[];
  comments?: string[];
  type?: string;
  reassigned_tip?: ReassignedTipInfo | null;
  bill?: {
    uuid: string;
    name: string;
    status: number;
  };
}

export interface ReassignedTipInfo {
  concession: string;
  amount: number;
}

export interface Payment {
  uuid: string;
  code: string;
  subtotal: number;
  tip: number;
  total: number;
  paid_at: string;
  status: number;
  nfc_transaction?: NfcTransaction;
  card_transaction?: CardTransaction;
  qr_transaction?: QrTransaction;
  palm_transaction?: PalmTransaction;
  cash_transaction?: CashTransaction;
}

interface PalmTransaction {
  uuid: string;
  terminal: string;
  palm_fingerprint?: {
    uuid: string;
    customer?: {
      phone_number: string;
    };
  };
}

interface CardTransaction {
  uuid: string;
  transaction_id: string;
  authorization: string;
  pin_used: number;
  approved: number;
  has_refund: number;
  rejection_reason: string;
  operation_key: string;
  afiliation: string;
  sale_key: string;
  terminal: string;
  entry_mode: string;
  longitude: string;
  latitude: string;
  type: string;
  sale_code: string;
  is_reversed: number;
  is_cancelled: number;
  date: string;
  application_date: string;
  currency: string;
  card: Card;
  created_at: string;
  merchant?: string;
}

export interface QrTransaction {
  qr_ticket: QrTicket;
  qr_wallet: QrWallet;
  subtotal: number;
  terminal: string;
  tip: number;
  total: number;
  type: string;
  uuid: string;
  venue_wallet: VenueWallet;
}

interface QrTicket {
  bin: string | null;
  current_balance: number | null;
  uuid: string | null;
}

interface QrWallet {
  customer: Customer;
  name: string | null;
  uuid: string | null;
}

interface Customer {
  last_name: string | null;
  name: string | null;
  uuid: string | null;
}

interface VenueWallet {
  uuid: string;
  venue: Venue;
}

interface Venue {
  uuid: string;
  name: string;
}

interface CashTransaction {
  uuid: string;
}

export interface SalesHistoryItem {
  concession_uuid: string;
  concession_name: string;
  event_uuid: string;
  event_name: string;
  event_starts_at: string;
  sales_quantity: number;
  sales_products_quantity: number;
  sales_subtotal: number;
}

export interface SalesHistoryResponse {
  data: SalesHistoryItem[];
}

export interface SalesHistoryQueries {
  concession?: string;
  event?: string;
  venue?: string;
  is_system?: string;
}

export interface CompleteSalePaymentInput {
  subtotal: number;
  tip: number;
  payment_type: "card" | "cash";
  merchant?: string;
}

export interface CompleteSaleBody {
  payments: CompleteSalePaymentInput[];
}

export interface PartialCancelProduct {
  uuid_sale_product: string;
  quantity: number;
  /**
   * Decisión del operador sobre si el producto debe regresar al stock al cancelar.
   * El backend solo respeta este valor cuando la request proviene de Dashboard
   * (identificado vía headers X-App-Platform / X-App-Name).
   */
  restock?: boolean;
}

/**
 * Decisión de reinventario por producto enviada al endpoint de cancelación total.
 * El backend la respeta únicamente cuando la request proviene de Dashboard;
 * los otros orígenes (POS) ignoran este campo y conservan el comportamiento de reinventario automático.
 */
export interface CancelSaleProductRestock {
  uuid_sale_product: string;
  restock: boolean;
}

export interface PartialCancelPayment {
  uuid: string;
  refund_amount: number;
}

export interface PartialCancelSaleBody {
  products: PartialCancelProduct[];
  payments?: PartialCancelPayment[];
  phone_number?: string;
  comment: string;
}

/**
 * YOSHI-10261: producto de reemplazo válido para un producto ya registrado en una venta / cuenta.
 * El backend solo devuelve productos activos de la misma concesión y del mismo precio exacto.
 */
export interface ProductChangeOption {
  uuid: string;
  name: string;
  price: number;
  image: string | null;
  uuid_category: string | null;
  category: string | null;
}

/**
 * YOSHI-10261: producto de la venta / cuenta que se puede cambiar, con sus opciones de reemplazo.
 * Los complementos (addons) no se incluyen: se administran desde su producto principal.
 */
export interface ProductChangeCandidate {
  uuid_sale_product: string;
  uuid_sale: string;
  uuid_product: string;
  product: string;
  quantity: number;
  price: number;
  uuid_concession: string;
  name_concession: string;
  options: ProductChangeOption[];
}

/**
 * YOSHI-10261: cuerpo del endpoint de cambio de producto. Cada elemento cambia un producto de la
 * venta / cuenta por otro producto; el cobro no se altera porque el precio es idéntico.
 */
export interface ChangeProductBody {
  products: { uuid_sale_product: string; uuid_product: string }[];
}
